import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import Icon from '../components/Icon';
import Reveal from '../components/Reveal';
import BRWStrip from '../components/BRWStrip';
import useSeo from '../hooks/useSeo';
import { IMAGES, PEOPLE, PEOPLE_HIGHLIGHTS, PEOPLE_STATS } from '../mock';

const OurPeople = () => {
  useSeo({
    title: 'Our People | Duffy Investment Group',
    description: 'Meet the people behind Duffy Investment Group and the experience supporting its investment activities across diverse industries and international markets.',
    keywords: 'Duffy Investment Group team, investment professionals, business leadership, investment experience, global business experience',
  });

  const [idx, setIdx] = useState(0);
  const person = PEOPLE[idx];
  const others = PEOPLE.map((p, i) => ({ ...p, i })).filter((p) => p.i !== idx);

  const switchTo = (i) => {
    setIdx(i);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div>
      {/* ===================== HERO ===================== */}
      <section className="relative bg-navy-grad overflow-hidden">
        <div className="absolute inset-0 lg:left-[42%]">
          <img key={person.id} src={IMAGES[person.heroImg]} alt={person.name} className="w-full h-full craig-hero-img" />
          <div className="absolute inset-0 lg:hidden" style={{ background: 'linear-gradient(180deg, rgba(7,19,34,0.15) 0%, rgba(7,19,34,0) 28%, rgba(7,19,34,0.55) 52%, rgba(7,19,34,0.96) 100%)' }} />
          <div className="absolute inset-0 hidden lg:block" style={{ background: 'linear-gradient(90deg, rgba(10,26,48,1) 0%, rgba(10,26,48,0.6) 16%, rgba(10,26,48,0) 42%)' }} />
          {person.heroTinted && <div className="hero-br-tint" />}
        </div>
        <div className="relative dig-container">
          <div className="flex flex-col justify-end lg:justify-center min-h-[82vh] lg:min-h-[560px]">
            <div className="pb-9 lg:pb-0 max-w-xl">
              {person.heroType === 'statement' ? (
                <Reveal>
                  <h1 className="font-serif-dig text-white" style={{ fontSize: 'clamp(1.85rem, 6vw, 3.4rem)', fontWeight: 700, lineHeight: 1.1 }}>
                    LEADERSHIP.<br />EXPERIENCE.<br /><span className="gold-text">RESULTS.</span>
                  </h1>
                  <span className="gold-rule-lg block my-6" />
                  <p className="font-serif-dig text-white" style={{ fontSize: '1.5rem', fontWeight: 700 }}>{person.name}</p>
                  <p className="font-body gold-text" style={{ fontWeight: 600 }}>{person.role}</p>
                  <p className="font-body" style={{ color: 'rgba(255,255,255,0.85)' }}>Duffy Investment Group</p>
                </Reveal>
              ) : (
                <Reveal>
                  <p className="eyebrow gold-text" style={{ letterSpacing: '0.22em' }}>OUR PEOPLE</p>
                  <h1 className="font-serif-dig text-white" style={{ fontSize: 'clamp(2.4rem, 7vw, 4rem)', fontWeight: 700, lineHeight: 1.02, marginTop: 10 }}>
                    {person.name.split(' ').map((w, i) => (<span key={i} className="block">{w}</span>))}
                  </h1>
                  <span className="gold-rule-lg block my-6" />
                  <p className="font-label gold-text" style={{ fontSize: '1.4rem', fontWeight: 700 }}>{person.role}</p>
                  <p className="font-body" style={{ color: 'rgba(255,255,255,0.85)' }}>Duffy Investment Group</p>
                </Reveal>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ===================== SWITCHER ===================== */}
      <section className="bg-navy">
        <div className="dig-container pb-10 pt-2 md:py-8">
          <div className="grid md:grid-cols-2 gap-4">
            {others.map((p) => (
              <button
                key={p.id}
                onClick={() => switchTo(p.i)}
                className="flex items-center justify-between text-left w-full transition-transform hover:-translate-y-0.5"
                style={{ border: '1px solid var(--gold)', padding: '18px 22px', background: 'rgba(255,255,255,0.02)' }}
              >
                <span>
                  <span className="font-serif-dig text-white block" style={{ fontSize: '1.35rem', fontWeight: 700 }}>{p.name}</span>
                  <span className="font-body block" style={{ color: 'rgba(255,255,255,0.8)' }}>{p.role}</span>
                </span>
                <ArrowRight size={26} style={{ color: 'var(--gold)', flexShrink: 0 }} />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== PROFILE ===================== */}
      <section className="bg-cream">
        <div className="dig-container py-16 md:py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <Reveal>
              <img
                key={person.profileImg}
                src={IMAGES[person.profileImg]}
                alt={person.name}
                className="w-full h-auto object-cover"
                style={{ maxWidth: 400, border: '3px solid var(--gold)' }}
              />
              {person.profileCaption && (
                <p className="font-body mt-3" style={{ color: 'var(--muted)', fontSize: '0.9rem', lineHeight: 1.5, whiteSpace: 'pre-line' }}>{person.profileCaption}</p>
              )}
            </Reveal>
            <Reveal delay={140}>
              <h2 className="font-serif-dig gold-text" style={{ fontSize: 'clamp(1.6rem, 3vw, 2.1rem)', fontWeight: 700, lineHeight: 1.2, textTransform: person.profileHeadingSerifCaps ? 'uppercase' : 'none' }}>
                {person.profileHeading}
              </h2>
              {person.profileSubHeading && (
                <p className="font-serif-dig navy-text" style={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2, marginTop: '0.35rem' }}>
                  {person.profileSubHeading}
                </p>
              )}
              {person.bio.map((para, i) => (
                <p key={i} className="font-body mt-5" style={{ color: i === 0 ? 'var(--ink)' : 'var(--muted)', lineHeight: 1.75, fontSize: '1.05rem' }}>{para}</p>
              ))}
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===================== KEY HIGHLIGHTS (Craig only) ===================== */}
      {person.showHighlights && (
        <section className="bg-cream-2">
          <div className="dig-container py-16 md:py-20">
            <Reveal className="flex items-center justify-center gap-5">
              <span className="gold-rule hidden sm:block" />
              <h2 className="section-title text-center" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.1rem)' }}>KEY INVESTMENT HIGHLIGHTS</h2>
              <span className="gold-rule hidden sm:block" />
            </Reveal>
            <div className="max-w-4xl mx-auto mt-12">
              {PEOPLE_HIGHLIGHTS.map((h, i) => (
                <Reveal key={i} delay={i * 60} className="flex items-start gap-5 py-5">
                  <div className="flex items-start gap-5 w-full" style={{ borderBottom: i < PEOPLE_HIGHLIGHTS.length - 1 ? '1px solid rgba(0,0,0,0.08)' : 'none', paddingBottom: '1.25rem' }}>
                    <div className="grid place-items-center rounded-full shrink-0" style={{ width: 58, height: 58, background: 'var(--navy)' }}>
                      <Icon name={h.icon} size={26} strokeWidth={1.5} style={{ color: 'var(--gold)' }} />
                    </div>
                    <p className="font-body flex-1" style={{ color: 'var(--ink)', lineHeight: 1.65 }}>{h.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <BRWStrip />

      {/* ===================== STATS (Craig only) ===================== */}
      {person.showStats && (
        <section style={{ background: 'var(--navy-deep)' }}>
          <div className="dig-container py-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-y-10">
              {PEOPLE_STATS.map((s, i) => (
                <div key={s.label} className={`flex items-center gap-4 px-2 md:px-6 ${i > 0 ? 'md:border-l' : ''}`} style={{ borderColor: 'rgba(255,255,255,0.12)' }}>
                  <Icon name={s.icon} size={38} strokeWidth={1.2} style={{ color: 'var(--gold)', flexShrink: 0 }} />
                  <div>
                    <p className="font-serif-dig gold-text" style={{ fontSize: '1.7rem', fontWeight: 800, lineHeight: 1 }}>{s.value}</p>
                    <p className="font-label" style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.08em', marginTop: 4 }}>{s.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default OurPeople;
